import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import {
  commercialClientsQueryKeys,
  commercialMeetingsQueryKeys,
} from "../query-keys";
import {
  CommercialClientCommentForm,
  CommercialMeetingForm,
  CommercialMeetingUpdateForm,
} from "../schemas";
import { CommercialMeeting } from "@ac-expo/api-types";
import { CommercialClient } from "@prisma/client";
import { isAxiosError } from "axios";

async function createCommercialMeeting(
  structureId: string,
  body: CommercialMeetingForm
) {
  try {
    const res = await privateAxios.post<CommercialMeeting>(
      `/exhibitor-commercial/${structureId}/meeting`,
      body
    );
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
}

export function useCreateCommercialMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      structureId,
      data,
    }: {
      structureId: string;
      data: CommercialMeetingForm;
    }) => createCommercialMeeting(structureId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialMeetingsQueryKeys.all,
      });
    },
  });
}

async function updateCommercialMeeting(
  id: string,
  structureId: string,
  body: CommercialMeetingUpdateForm
) {
  try {
    const res = await privateAxios.patch<CommercialMeeting>(
      `/exhibitor-commercial/${structureId}/meeting/${id}`,
      body
    );
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
}

export function useUpdateCommercialMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      structureId,
      data,
    }: {
      id: string;
      structureId: string;
      data: CommercialMeetingUpdateForm;
    }) => updateCommercialMeeting(id, structureId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialMeetingsQueryKeys.all,
      });
    },
  });
}

async function deleteCommercialMeeting(id: string, structureId: string) {
  const res = await privateAxios.delete<CommercialMeeting>(
    `/exhibitor-commercial/${structureId}/meeting/${id}`
  );
  return res.data;
}

export function useDeleteCommercialMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, structureId }: { id: string; structureId: string }) =>
      deleteCommercialMeeting(id, structureId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commercialMeetingsQueryKeys.all,
      });
    },
  });
}

async function setCommercialClientComment(
  clientd: string,
  structureId: string,
  eventId: string,
  body: CommercialClientCommentForm
) {
  const res = await privateAxios.put<CommercialClient>(
    `/exhibitor-commercial/${structureId}/${eventId}/client/${clientd}`,
    body
  );
  return res.data;
}

export function useSetCommercialClientComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      structureId,
      eventId,
      body,
    }: {
      clientId: string;
      structureId: string;
      eventId: string;
      body: CommercialClientCommentForm;
    }) => setCommercialClientComment(clientId, structureId, eventId, body),
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: commercialClientsQueryKeys.byId(variables.id),
      });
    },
  });
}
